import api from '../services/api';
import { MaterialType } from './materials';
import { Vendor } from './vendor';

export enum PurchaseOrderStatus {
  DRAFT = 'DRAFT',
  SENT = 'SENT',
  PARTIALLY_FULFILLED = 'PARTIALLY_FULFILLED',
  FULFILLED = 'FULFILLED',
  CLOSED = 'CLOSED',
  CANCELLED = 'CANCELLED'
}

export enum POItemStatus {
  PENDING = 'PENDING',
  PARTIALLY_FULFILLED = 'PARTIALLY_FULFILLED',
  FULFILLED = 'FULFILLED'
}

export interface PurchaseOrderItem {
  id?: number;
  material_type: MaterialType;
  material_quality?: string | null;
  description?: string | null;
  quantity: number;
  uom: string;
  rate: number;
  amount: number;
  image_url?: string | null;
  fabric_width?: number | null;
  fabric_L?: number | null;
  is_sample?: boolean;
  garment_part?: string | null;
  status?: POItemStatus;
  fulfilled_qty?: number;
  remaining_qty?: number;
}

export interface PurchaseOrder {
  id: number;
  po_number: string;
  vendor_id: number;
  company_id: number;
  notes?: string | null;
  terms?: string | null;
  valid_until?: string | null;
  created_by: string;
  status: PurchaseOrderStatus;
  created_at: string;
  updated_at: string;
  vendor?: Vendor;
  items?: PurchaseOrderItem[];
  _count?: {
    purchases: number;
  };
}

export interface CreatePurchaseOrderDTO {
  vendor_id: number;
  notes?: string;
  terms?: string;
  valid_until?: string | null;
  items: Array<{
    material_type: MaterialType;
    material_quality?: string | null;
    description?: string | null;
    quantity: number;
    uom: string;
    rate: number;
    amount: number;
    image_url?: string;
    fabric_width?: number | null;
    fabric_L?: number | null;
    is_sample?: boolean;
    garment_part?: string | null;
  }>;
}

export interface PurchaseOrderListResponse {
  data: PurchaseOrder[];
  total: number;
  page: number;
  pageSize: number;
}

export interface VendorAvailablePOItem extends PurchaseOrderItem {
  remaining_qty: number;
}

export interface VendorAvailablePO {
  id: number;
  po_number: string;
  status: PurchaseOrderStatus;
  created_at: string;
  valid_until?: string | null;
  items: VendorAvailablePOItem[];
}

export const purchaseOrdersApi = {
  getAll: async (params?: {
    vendor_id?: number;
    status?: PurchaseOrderStatus;
    page?: number;
    pageSize?: number;
    q?: string;
    include_cancelled?: boolean;
    design_no?: string;
  }) => {
    return await api.get<PurchaseOrderListResponse | PurchaseOrder[]>('/purchase-orders', { params });
  },

  getById: async (id: number) => {
    return await api.get<PurchaseOrder>(`/purchase-orders/${id}`);
  },

  create: async (data: CreatePurchaseOrderDTO) => {
    return await api.post<PurchaseOrder>('/purchase-orders', data);
  },

  update: async (id: number, data: CreatePurchaseOrderDTO) => {
    return await api.put<PurchaseOrder>(`/purchase-orders/${id}`, data);
  },

  updateStatus: async (id: number, status: PurchaseOrderStatus) => {
    return await api.patch(`/purchase-orders/${id}/status`, { status });
  },

  updateItemStatuses: async (id: number, items: { id: number; status: POItemStatus }[]) => {
    return await api.patch<PurchaseOrder>(`/purchase-orders/${id}/item-statuses`, { items });
  },

  delete: async (id: number) => {
    return await api.delete(`/purchase-orders/${id}`);
  },

  getMaterialQualities: async () => {
    return await api.get<string[]>('/purchase-orders/material-qualities');
  },

  getVendorAvailableItems: async (vendorId: number) => {
    return await api.get<VendorAvailablePO[]>(`/purchase-orders/vendor/${vendorId}/available-items`);
  }
};

export default purchaseOrdersApi;


